/**
 * 检查项配置 - 两步向导（v2 项目视角）
 * UI 严格对齐 web后台.html v2 ConfigWizard
 *
 * Step1：项目名称 + 基地名称 + 整机编号（Input + 添加 + closable tags）
 * Step2：顶部摘要 + 搜索 + 全选 + 已选 N/总数 + 2 列 checkbox 网格（maxHeight 340 滚动）
 *
 * 数据模型：直接对应 mingyang_project 接口的 ProjectListItem +
 *           knowledges 字段（逗号分隔的 knowledge_id 列表）
 */
import React, { useEffect, useMemo, useState } from 'react'
import { Button, Input, Modal, Spin, message } from 'antd'
import {
  CheckOutlined,
  ExclamationCircleOutlined,
  SearchOutlined
} from '@ant-design/icons'
import { selectGroupAndUser } from '../api/client'
import {
  ProjectListItem,
  createProject,
  fetchProjectDetail,
  updateProject
} from '../api/project'
import { KnowledgeOption, fetchKnowledgeOptions } from '../api/knowledge'

interface ConfigWizardProps {
  /** null = 新增；ProjectListItem = 编辑 */
  row: ProjectListItem | null
  dispatch: (...args: any[]) => any
  state: any
  onClose: () => void
  onSuccess: () => void
}

interface FormState {
  project_id?: number
  project_name: string
  base_name: string
  codes: string[]
  /** 已选 knowledge_id 列表 */
  checkItems: number[]
}

function emptyForm(): FormState {
  return {
    project_name: '',
    base_name: '',
    codes: [],
    checkItems: []
  }
}

function rowToForm(row: ProjectListItem): FormState {
  return {
    project_id: row.project_id,
    project_name: row.project_name || '',
    base_name: row.base_name || '',
    codes: (row.project_numbers || '')
      .split(',')
      .map((c) => c.trim())
      .filter(Boolean),
    checkItems: (row.knowledges || '')
      .split(',')
      .map((s) => Number(s.trim()))
      .filter((n) => Number.isFinite(n) && n > 0)
  }
}

const ConfigWizard: React.FC<ConfigWizardProps> = ({
  row,
  dispatch,
  state,
  onClose,
  onSuccess
}) => {
  const isNew = !row
  const [step, setStep] = useState(1)
  const [form, setForm] = useState<FormState>(
    row ? rowToForm(row) : emptyForm()
  )
  const [codeInput, setCodeInput] = useState('')
  const [searchQ, setSearchQ] = useState('')
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [knowledgeOptions, setKnowledgeOptions] = useState<KnowledgeOption[]>(
    []
  )

  // 编辑模式：拉详情补回数据（处理 list 接口没返回 knowledges 的情况）
  useEffect(() => {
    if (!row?.project_id) return
    setLoading(true)
    fetchProjectDetail(dispatch, row.project_id)
      .then((d) => {
        setForm({
          project_id: d.project_id,
          project_name: d.project_name || '',
          base_name: d.base_name || '',
          codes: (d.project_numbers || '')
            .split(',')
            .map((c) => c.trim())
            .filter(Boolean),
          checkItems: (d.knowledges || '')
            .split(',')
            .map((s) => Number(s.trim()))
            .filter((n) => Number.isFinite(n) && n > 0)
        })
      })
      .catch(() => {
        /* no-op */
      })
      .finally(() => setLoading(false))
  }, [row, dispatch])

  // 拉全量检查项作为 Step2 候选
  useEffect(() => {
    fetchKnowledgeOptions(dispatch, { status: 1 })
      .then((d) => setKnowledgeOptions(d.list || []))
      .catch(() => {
        /* no-op */
      })
  }, [dispatch])

  // 整机编号 Input：添加/删除
  const addCode = () => {
    const trimmed = codeInput.trim().toUpperCase()
    if (trimmed && !form.codes.includes(trimmed)) {
      setForm((f) => ({ ...f, codes: [...f.codes, trimmed] }))
    }
    setCodeInput('')
  }
  const removeCode = (c: string) =>
    setForm((f) => ({ ...f, codes: f.codes.filter((x) => x !== c) }))

  // Step1 校验：项目名 + 基地 + 至少 1 个整机编号
  const step1Valid =
    !!form.project_name.trim() &&
    !!form.base_name.trim() &&
    form.codes.length > 0

  // Step2 校验：至少选一个检查项
  const step2Valid = form.checkItems.length > 0

  // 检查项过滤
  const filteredItems = useMemo(() => {
    if (!searchQ.trim()) return knowledgeOptions
    return knowledgeOptions.filter((k) => k.inspection.includes(searchQ.trim()))
  }, [knowledgeOptions, searchQ])

  const checkItemSet = useMemo(
    () => new Set(form.checkItems),
    [form.checkItems]
  )

  const allFilteredSelected =
    filteredItems.length > 0 &&
    filteredItems.every((k) => checkItemSet.has(k.knowledge_id))

  const toggleAll = () => {
    if (allFilteredSelected) {
      const filteredIds = new Set(filteredItems.map((k) => k.knowledge_id))
      setForm((f) => ({
        ...f,
        checkItems: f.checkItems.filter((id) => !filteredIds.has(id))
      }))
    } else {
      const s = new Set(form.checkItems)
      filteredItems.forEach((k) => s.add(k.knowledge_id))
      setForm((f) => ({ ...f, checkItems: Array.from(s) }))
    }
  }

  const toggleItem = (id: number) => {
    setForm((f) => {
      const s = new Set(f.checkItems)
      if (s.has(id)) s.delete(id)
      else s.add(id)
      return { ...f, checkItems: Array.from(s) }
    })
  }

  const handleSave = async () => {
    const { group_id, user_id } = selectGroupAndUser(state)
    if (!group_id || !user_id) {
      message.error('未获取到团队或用户信息')
      return
    }
    setSubmitting(true)
    try {
      if (isNew) {
        await createProject(dispatch, {
          project_name: form.project_name.trim(),
          base_name: form.base_name.trim(),
          project_numbers: form.codes.join(','),
          knowledges: form.checkItems.join(','),
          group_id,
          user_id
        })
        message.success('已新增并启用')
      } else {
        await updateProject(dispatch, {
          project_id: form.project_id!,
          project_name: form.project_name.trim(),
          base_name: form.base_name.trim(),
          project_numbers: form.codes.join(','),
          knowledges: form.checkItems.join(','),
          group_id,
          user_id
        })
        message.success('已保存修改')
      }
      onSuccess()
    } catch (e) {
      message.error((e as Error)?.message || '保存失败')
    } finally {
      setSubmitting(false)
    }
  }

  // 步骤条
  const stepperBar = (
    <div className="myse-stepper">
      {[
        { n: 1, label: '项目基础信息' },
        { n: 2, label: '选择检查项' }
      ].map((s, i) => {
        const done = step > s.n
        const cur = step === s.n
        return (
          <React.Fragment key={s.n}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span
                className={`myse-stepper__dot${
                  done ? ' myse-stepper__dot--done' : ''
                }${cur ? ' myse-stepper__dot--current' : ''}`}
              >
                {done ? <CheckOutlined /> : s.n}
              </span>
              <span
                className="myse-stepper__label"
                style={{
                  color: cur ? '#1F2937' : '#6B7280',
                  fontWeight: cur ? 600 : 400
                }}
              >
                {`第${s.n === 1 ? '一' : '二'}步 · ${s.label}`}
              </span>
            </div>
            {i === 0 && (
              <span
                className="myse-stepper__line"
                style={{
                  background: step > 1 ? '#10A66B' : '#E5E7EB'
                }}
              />
            )}
          </React.Fragment>
        )
      })}
    </div>
  )

  const footer = (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <div
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          fontSize: 12,
          color: '#9CA3AF'
        }}
      >
        <ExclamationCircleOutlined style={{ color: '#F59E0B' }} />
        {step === 1 ? '请先完成项目基础信息' : '为项目选择需要执行的检查项'}
      </div>
      {step === 2 && <Button onClick={() => setStep(1)}>上一步</Button>}
      <Button onClick={onClose} style={{ marginLeft: 8 }}>
        取消
      </Button>
      {step === 1 ? (
        <Button
          type="primary"
          disabled={!step1Valid}
          onClick={() => setStep(2)}
          style={{ marginLeft: 8 }}
        >
          下一步
        </Button>
      ) : (
        <Button
          type="primary"
          disabled={!step2Valid}
          loading={submitting}
          onClick={handleSave}
          style={{ marginLeft: 8 }}
        >
          {isNew ? '保存并启用' : '保存修改'}
        </Button>
      )}
    </div>
  )

  return (
    <Modal
      open
      title={
        <span>
          {isNew ? '新增项目' : '编辑项目'}
          {!isNew && (
            <span
              style={{
                fontSize: 12,
                color: '#9CA3AF',
                fontWeight: 400,
                marginLeft: 8
              }}
            >
              修改后立即生效，无法恢复历史
            </span>
          )}
        </span>
      }
      width={680}
      destroyOnHidden
      mask={{ closable: false }}
      onCancel={onClose}
      footer={footer}
    >
      <Spin spinning={loading || submitting}>
        {stepperBar}

        {/* Step 1：项目基础信息 */}
        {step === 1 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <FieldLabel label="项目名称" required>
              <Input
                value={form.project_name}
                onChange={(e) => {
                  // React 16 合成事件池：函数式 setState 异步执行前
                  // e.target 可能已被回收，必须同步取值
                  const v = e.target.value
                  setForm((f) => ({ ...f, project_name: v }))
                }}
                placeholder="填写项目名称"
                maxLength={100}
              />
            </FieldLabel>

            <FieldLabel label="基地名称" required>
              <Input
                value={form.base_name}
                onChange={(e) => {
                  const v = e.target.value
                  setForm((f) => ({ ...f, base_name: v }))
                }}
                placeholder="填写基地名称"
                maxLength={50}
              />
            </FieldLabel>

            <div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'baseline',
                  marginBottom: 8
                }}
              >
                <span
                  style={{
                    fontSize: 13,
                    color: '#374151',
                    fontWeight: 500
                  }}
                >
                  <span style={{ color: '#E84C4C', marginRight: 3 }}>*</span>
                  整机编号
                </span>
                <span style={{ fontSize: 12, color: '#9CA3AF' }}>
                  已添加 {form.codes.length} 个
                </span>
              </div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                <Input
                  value={codeInput}
                  onChange={(e) => setCodeInput(e.target.value)}
                  placeholder="输入编号，如 AG01"
                  style={{ flex: 1 }}
                  onPressEnter={(e) => {
                    e.preventDefault()
                    addCode()
                  }}
                />
                <Button onClick={addCode} disabled={!codeInput.trim()}>
                  添加
                </Button>
              </div>
              {form.codes.length > 0 && (
                <div
                  style={{
                    border: '1px solid #EEF0F3',
                    borderRadius: 8,
                    padding: 12,
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 6
                  }}
                >
                  {form.codes.map((c) => (
                    <span
                      key={c}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        padding: '4px 10px',
                        background: '#EFF6FF',
                        border: '1px solid #BFDBFE',
                        borderRadius: 4,
                        fontSize: 13,
                        color: '#1E40AF',
                        fontFamily: 'Inter, system-ui'
                      }}
                    >
                      {c}
                      <span
                        onClick={() => removeCode(c)}
                        style={{
                          cursor: 'pointer',
                          color: '#93C5FD',
                          fontSize: 14,
                          lineHeight: 1
                        }}
                      >
                        ×
                      </span>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Step 2：选择检查项 */}
        {step === 2 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {/* 摘要条 */}
            <div
              style={{
                background: '#F9FAFB',
                borderRadius: 6,
                padding: '10px 12px',
                fontSize: 12,
                color: '#6B7280',
                display: 'flex',
                justifyContent: 'space-between'
              }}
            >
              <span>
                <span style={{ color: '#9CA3AF' }}>项目：</span>
                <span style={{ color: '#1F2937', fontWeight: 500 }}>
                  {form.project_name}
                </span>
              </span>
              <span>
                <span style={{ color: '#9CA3AF' }}>基地：</span>
                <span style={{ color: '#1F2937' }}>{form.base_name}</span>
              </span>
              <span>
                <span style={{ color: '#9CA3AF' }}>编号数：</span>
                <span style={{ color: '#1F2937' }}>{form.codes.length}</span>
              </span>
            </div>

            {/* 搜索 + 全选 */}
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <div style={{ flex: 1 }}>
                <Input
                  value={searchQ}
                  onChange={(e) => setSearchQ(e.target.value)}
                  placeholder="搜索检查项名称"
                  prefix={<SearchOutlined style={{ color: '#9CA3AF' }} />}
                  allowClear
                />
              </div>
              <a
                onClick={toggleAll}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  fontSize: 13,
                  color: '#3B7DEB'
                }}
              >
                <span
                  style={{
                    width: 14,
                    height: 14,
                    borderRadius: 3,
                    border: `1.5px solid ${
                      allFilteredSelected ? '#3B7DEB' : '#CBD5E1'
                    }`,
                    background: allFilteredSelected ? '#3B7DEB' : '#fff',
                    display: 'grid',
                    placeItems: 'center'
                  }}
                >
                  {allFilteredSelected && (
                    <CheckOutlined style={{ color: '#fff', fontSize: 9 }} />
                  )}
                </span>
                全选
              </a>
              <span style={{ fontSize: 12, color: '#9CA3AF' }}>
                已选{' '}
                <span style={{ color: '#3B7DEB', fontWeight: 600 }}>
                  {form.checkItems.length}
                </span>{' '}
                / {knowledgeOptions.length}
              </span>
            </div>

            {/* 检查项 2 列网格，可滚动 */}
            <div
              style={{
                border: '1px solid #EEF0F3',
                borderRadius: 8,
                padding: 14,
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: 8,
                maxHeight: 340,
                overflowY: 'auto'
              }}
            >
              {filteredItems.map((k) => {
                const sel = checkItemSet.has(k.knowledge_id)
                return (
                  <div
                    key={k.knowledge_id}
                    onClick={() => toggleItem(k.knowledge_id)}
                    style={{
                      padding: '10px 12px',
                      borderRadius: 6,
                      cursor: 'pointer',
                      border: `1px solid ${sel ? '#3B7DEB' : '#E5E7EB'}`,
                      background: sel ? '#EFF6FF' : '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      fontSize: 13,
                      color: sel ? '#1E40AF' : '#374151',
                      transition: 'all .15s'
                    }}
                  >
                    <span
                      style={{
                        width: 16,
                        height: 16,
                        borderRadius: 3,
                        border: `1.5px solid ${sel ? '#3B7DEB' : '#CBD5E1'}`,
                        background: sel ? '#3B7DEB' : '#fff',
                        display: 'grid',
                        placeItems: 'center',
                        flexShrink: 0
                      }}
                    >
                      {sel && (
                        <CheckOutlined
                          style={{ color: '#fff', fontSize: 10 }}
                        />
                      )}
                    </span>
                    {k.inspection}
                  </div>
                )
              })}
              {filteredItems.length === 0 && (
                <div
                  style={{
                    gridColumn: '1 / -1',
                    padding: 30,
                    textAlign: 'center',
                    color: '#9CA3AF'
                  }}
                >
                  没有匹配的检查项
                </div>
              )}
            </div>
          </div>
        )}
      </Spin>
    </Modal>
  )
}

const FieldLabel: React.FC<{
  label: React.ReactNode
  required?: boolean
  children: React.ReactNode
}> = ({ label, required, children }) => (
  <div>
    <div style={{ marginBottom: 6 }}>
      <span style={{ fontSize: 13, color: '#374151', fontWeight: 500 }}>
        {required && (
          <span style={{ color: '#E84C4C', marginRight: 3 }}>*</span>
        )}
        {label}
      </span>
    </div>
    {children}
  </div>
)

export default ConfigWizard
